/**
 * Order photos — what the help flow's "Quiero ver las fotos de un pedido" shows.
 *
 * Why this exists: it was the most repeated request in the help chat (ops, sep-2026). The kitchen
 * wants to see what the camera saw — to understand a "missing item", to check whether they put
 * something in the bag, or simply because the photos are taken half blind — and until now the
 * only way was to ask support, who went to the Hub and sent a screenshot back, often to another
 * device. The tablet can read the photos itself: devices have SELECT on `order`, `photo` and
 * `order_analysis` of their own restaurant (RLS), and `photo.url` points at a public object, so
 * an `<img>` loads it as-is.
 *
 * Pure on purpose (no Vue, no GraphQL client), so the shape the list and the viewer depend on is
 * unit-tested against the recorded API shape: the server's `Order` becomes the same descriptor
 * the rest of the help flow already uses (describeOrder), plus the photos and when they were
 * taken — the moment the kitchen remembers ("the one I did five minutes ago"), not the POS time.
 */
import { describeOrder } from '@/helpFlow.js';

/** Orders per page: four rows of two tiles, the most the pass tablet shows without scrolling. */
export const PHOTO_HISTORY_PAGE = 8;
/** "See older ones" stops here — past that, the Hub is the tool, not a glove-scrolled list. */
export const PHOTO_HISTORY_MAX = 40;

/**
 * The server stores `timestamp` without zone in places; AppSync usually hands it back with `Z`,
 * but a bare value has to be read as UTC too (the same guard CameraScreen uses for rider times),
 * or a Madrid tablet would place every photo two hours in the future.
 */
export function parseServerTime(value) {
  if (!value) return null;
  let text = String(value);
  if (!text.endsWith('Z') && !/[+-]\d{2}:?\d{2}$/.test(text)) text = `${text}Z`;
  const ms = Date.parse(text);
  return Number.isNaN(ms) ? null : ms;
}

/**
 * One server `Order` (the `orders` / `order` queries) as the descriptor the help flow uses, plus
 * its photos in the order they were taken. The code follows the pass tile's rule through
 * describeOrder (ticket → delivery → POS id), so the tile in the list reads like the ticket on
 * the bag; `posProviderId` is the same value the realtime rows call `posId` (checked on the
 * orders that have neither ticket nor delivery id).
 * @param {object} order  server Order: `{ id, ticketId, deliveryId, posProviderId, deliveryPlatform,
 *   createdAt, manualIsCorrect, isManualInsert, photos: [{ url, createdAt }], orderAnalyses: [...] }`
 * @param {object} [opts]
 * @param {(platform: string) => string} [opts.platformLabel]
 * @param {boolean} [opts.aiOff]  restaurant.proActivated === false
 */
export function describePhotoOrder(order, { platformLabel = null, aiOff = false } = {}) {
  if (!order || !order.id) return null;
  const photos = (order.photos || [])
    .filter((photo) => photo && typeof photo.url === 'string' && photo.url)
    .map((photo) => ({ url: photo.url, takenAt: parseServerTime(photo.createdAt) }))
    .sort((a, b) => (a.takenAt ?? 0) - (b.takenAt ?? 0));
  const row = {
    orderId: order.id,
    ticketId: order.ticketId || null,
    deliveryId: order.deliveryId || null,
    posId: order.posProviderId || null,
    deliveryPlatform: order.deliveryPlatform || null,
    photoCount: photos.length,
    _isManual: order.isManualInsert === true,
    order: {
      manualIsCorrect: order.manualIsCorrect ?? null,
      orderAnalyses: order.orderAnalyses || [],
      photos,
    },
  };
  const described = describeOrder(row, platformLabel, { aiOff });
  const takenTimes = photos.map((photo) => photo.takenAt).filter((ms) => ms !== null);
  return {
    ...described,
    createdAt: parseServerTime(order.createdAt),
    lastPhotoAt: takenTimes.length ? Math.max(...takenTimes) : null,
    photos,
  };
}

/**
 * Most recently photographed first. The server pages by creation time (it has the index for it);
 * the kitchen remembers the moment they took the photo, and a scheduled order made now was
 * created an hour ago. Stable for equal times, so the list does not shuffle between renders.
 */
export function sortByLastPhoto(list) {
  const when = (entry) => entry.lastPhotoAt ?? entry.createdAt ?? 0;
  return list
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) => (when(b.entry) - when(a.entry)) || (a.index - b.index))
    .map(({ entry }) => entry);
}

/**
 * A page of server orders turned into list entries. Orders without a single usable photo are
 * dropped: `hasPhoto` can be true while the photo rows are still being written, and a tile that
 * opens onto nothing is exactly the dead end this list exists to remove.
 */
export function photoHistoryFromServer(orders, opts = {}) {
  return sortByLastPhoto(
    (orders || []).map((order) => describePhotoOrder(order, opts)).filter((d) => d && d.photos.length > 0),
  );
}

/**
 * A page of server orders WITHOUT a photo turned into list entries ("an order left without a
 * photo"). The same descriptor and the same order as the photo list — most recent first, by when
 * the order was created, since there is no photo time to go by — and none dropped: having no photo
 * is the point here.
 */
export function orderHistoryFromServer(orders, opts = {}) {
  return sortByLastPhoto((orders || []).map((order) => describePhotoOrder(order, opts)).filter(Boolean));
}

/**
 * The orders the AI marked as wrong first, everything else in the order it came. For "the app says
 * an item is missing and it is in the bag": the red one is the order the person is almost always
 * talking about. Stable, so two lists built from the same page never differ.
 */
export function incorrectFirst(list) {
  const rank = (entry) => (entry.verdict === 'incorrect' ? 0 : 1);
  return (list || [])
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) => (rank(a.entry) - rank(b.entry)) || (a.index - b.index))
    .map(({ entry }) => entry);
}

/**
 * Append an older page to what is already on screen. What is on screen does not move — a tile
 * jumping under the finger is how a tap lands on the wrong order — and an order the server
 * returns twice (the list shifted between pages while a new one arrived) appears once.
 */
export function appendPhotoPage(current, older) {
  const seen = new Set((current || []).map((entry) => entry.orderId));
  return [...(current || []), ...(older || []).filter((entry) => !seen.has(entry.orderId))];
}

/** Whether "see older ones" can still bring something: the last page came back full and the cap is not reached. */
export function canLoadMorePhotos({ loaded = 0, lastPageSize = 0, page = PHOTO_HISTORY_PAGE, max = PHOTO_HISTORY_MAX } = {}) {
  return lastPageSize >= page && loaded < max;
}

function dayKey(ms, timeZone) {
  // en-CA formats as YYYY-MM-DD: a sortable, comparable calendar day in the tablet's zone.
  return new Date(ms).toLocaleDateString('en-CA', { timeZone });
}

/**
 * When a photo was taken, in the words the kitchen uses: "hace 5 min" within the hour, the time
 * of day today, "ayer, 23:10", and a date beyond that. Relative up to an hour because that is how
 * a pass remembers ("the one before last"); absolute after, because "hace 3 h" means nothing on a
 * ticket that prints the time.
 * @param {number|null} ms
 * @param {object} args
 * @param {Function} args.t        vue-i18n translate
 * @param {string} [args.locale]   'es' | 'en' | 'fr' | 'pt'
 * @param {number} [args.now]
 * @param {string} [args.timeZone] for tests; the tablet's own zone by default
 */
export function photoTimeLabel(ms, { t, locale = 'es', now = Date.now(), timeZone } = {}) {
  if (ms === null || ms === undefined || Number.isNaN(ms)) return '';
  const minutes = Math.floor((now - ms) / 60000);
  if (minutes < 1) return t('message.orderPhotosJustNow');
  if (minutes < 60) return t('message.orderPhotosMinutesAgo', { minutes });
  const time = new Date(ms).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', timeZone });
  const day = dayKey(ms, timeZone);
  if (day === dayKey(now, timeZone)) return time;
  if (day === dayKey(now - 24 * 60 * 60 * 1000, timeZone)) return t('message.orderPhotosYesterday', { time });
  const date = new Date(ms).toLocaleDateString(locale, { day: '2-digit', month: '2-digit', timeZone });
  return t('message.orderPhotosDate', { date, time });
}
